import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";

export interface SupportThread {
  id: string;
  user_id: string;
  subject: string | null;
  category: string | null;
  status: "open" | "awaiting_user" | "resolved";
  assigned_admin_id: string | null;
  last_message_at: string;
  created_at: string;
  updated_at: string;
  user_name?: string;
  user_email?: string;
  user_avatar?: string;
  assigned_admin_name?: string;
  last_message_preview?: string;
  unread_count?: number;
}

export interface SupportMessage {
  id: string;
  thread_id: string;
  sender_role: "user" | "admin";
  sender_id: string;
  body: string;
  created_at: string;
  sender_name?: string;
  sender_avatar?: string;
}

// Hook to get user's support threads
export function useUserSupportThreads() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["support_threads", "user", user?.id],
    queryFn: async () => {
      if (!user?.id) return [];

      const { data: threads, error } = await supabase
        .from("support_threads")
        .select("*")
        .eq("user_id", user.id)
        .order("last_message_at", { ascending: false });

      if (error) throw error;

      // Get last message preview for each thread
      const threadsWithPreviews = await Promise.all(
        (threads || []).map(async (thread) => {
          const { data: messages } = await supabase
            .from("support_messages")
            .select("body")
            .eq("thread_id", thread.id)
            .order("created_at", { ascending: false })
            .limit(1);

          // Skip unread tracking - support_thread_reads table has RLS issues
          // Just show 0 unread for now to avoid 406 errors
          const unreadCount = 0;

          return {
            ...thread,
            last_message_preview: messages?.[0]?.body?.substring(0, 100) || "",
            unread_count: unreadCount,
          } as SupportThread;
        })
      );

      return threadsWithPreviews;
    },
    enabled: !!user?.id,
    refetchInterval: 30000,
  });
}

// Hook to get all support threads (admin)
export function useAdminSupportThreads(filters?: {
  status?: string;
  category?: string;
  assignedToMe?: boolean;
}) {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["support_threads", "admin", filters],
    queryFn: async () => {
      if (!user?.id) return [];

      let query = supabase
        .from("support_threads")
        .select("*")
        .order("last_message_at", { ascending: false });

      if (filters?.status && filters.status !== "all") {
        query = query.eq("status", filters.status);
      }
      if (filters?.category && filters.category !== "all") {
        query = query.eq("category", filters.category);
      }
      if (filters?.assignedToMe) {
        query = query.eq("assigned_admin_id", user.id);
      }

      const { data: threads, error } = await query;
      if (error) throw error;

      // Fetch user info for each thread
      const userIds = [...new Set((threads || []).map((t) => t.user_id))];
      const adminIds = [...new Set((threads || []).filter((t) => t.assigned_admin_id).map((t) => t.assigned_admin_id!))];

      const [profilesRes, privateRes, adminProfilesRes] = await Promise.all([
        supabase.from("profiles").select("id, full_name, avatar_url").in("id", userIds),
        supabase.from("profile_private").select("user_id, email").in("user_id", userIds),
        adminIds.length > 0
          ? supabase.from("profiles").select("id, full_name").in("id", adminIds)
          : Promise.resolve({ data: [] }),
      ]);

      const profiles = profilesRes.data || [];
      const privateData = privateRes.data || [];
      const adminProfiles = adminProfilesRes.data || [];

      // Get last message preview for each thread
      const threadsWithDetails = await Promise.all(
        (threads || []).map(async (thread) => {
          const { data: messages } = await supabase
            .from("support_messages")
            .select("body")
            .eq("thread_id", thread.id)
            .order("created_at", { ascending: false })
            .limit(1);

          const profile = profiles.find((p) => p.id === thread.user_id);
          const privateInfo = privateData.find((p) => p.user_id === thread.user_id);
          const adminProfile = thread.assigned_admin_id
            ? adminProfiles.find((p) => p.id === thread.assigned_admin_id)
            : null;

          return {
            ...thread,
            user_name: profile?.full_name || "Unknown",
            user_email: privateInfo?.email || "",
            user_avatar: profile?.avatar_url || "",
            assigned_admin_name: adminProfile?.full_name || null,
            last_message_preview: messages?.[0]?.body?.substring(0, 100) || "",
          } as SupportThread;
        })
      );

      return threadsWithDetails;
    },
    enabled: !!user?.id,
    refetchInterval: 30000,
  });
}

// Hook to get messages for a thread
export function useSupportMessages(threadId: string | null) {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["support_messages", threadId],
    queryFn: async () => {
      if (!threadId) return [];

      const { data: messages, error } = await supabase
        .from("support_messages")
        .select("*")
        .eq("thread_id", threadId)
        .order("created_at", { ascending: true });

      if (error) throw error;

      // Fetch sender info
      const senderIds = [...new Set((messages || []).map((m) => m.sender_id))];
      const { data: profiles } = await supabase
        .from("profiles")
        .select("id, full_name, avatar_url")
        .in("id", senderIds);

      return (messages || []).map((message) => {
        const profile = profiles?.find((p) => p.id === message.sender_id);
        return {
          ...message,
          sender_name: profile?.full_name || "Unknown",
          sender_avatar: profile?.avatar_url || "",
        } as SupportMessage;
      });
    },
    enabled: !!threadId && !!user?.id,
    refetchInterval: 10000,
  });
}

// Hook to get unread count for user
export function useSupportUnreadCount() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["support_unread_count", user?.id],
    queryFn: async () => {
      if (!user?.id) return 0;

      const { data, error } = await supabase.rpc("get_support_unread_count", {
        _user_id: user.id,
      });

      if (error) throw error;
      return data || 0;
    },
    enabled: !!user?.id,
    refetchInterval: 30000,
  });
}

// Hook to create a new thread
export function useCreateSupportThread() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({
      subject,
      category,
      message,
    }: {
      subject?: string;
      category?: string;
      message: string;
    }) => {
      if (!user?.id) throw new Error("Not authenticated");

      console.log("[Ask Nedra] Creating support thread for user:", user.id);

      // Get user's profile for the notification
      const { data: userProfile } = await supabase
        .from("profiles")
        .select("full_name")
        .eq("id", user.id)
        .single();

      // Create thread
      const { data: thread, error: threadError } = await supabase
        .from("support_threads")
        .insert({
          user_id: user.id,
          subject: subject || null,
          category: category || null,
        })
        .select()
        .single();

      if (threadError) {
        console.error("[Ask Nedra] Thread creation error:", threadError);
        throw threadError;
      }

      console.log("[Ask Nedra] Thread created:", thread.id);

      // Create first message
      const { error: messageError } = await supabase.from("support_messages").insert({
        thread_id: thread.id,
        sender_role: "user",
        sender_id: user.id,
        body: message,
      });

      if (messageError) {
        console.error("[Ask Nedra] Message creation error:", messageError);
        throw messageError;
      }

      console.log("[Ask Nedra] Message created successfully");

      // Create notifications for all admin users using SECURITY DEFINER function
      console.log("[Ask Nedra] Creating admin notifications via database function...");
      const userName = userProfile?.full_name || "A user";
      
      const { error: notificationError } = await supabase.rpc("create_ask_nedra_notifications", {
        p_thread_id: thread.id,
        p_thread_subject: subject || "New Request",
        p_submitter_name: userName,
      });

      if (notificationError) {
        console.error("[Ask Nedra] ❌ Notification creation FAILED:", notificationError);
      } else {
        console.log("[Ask Nedra] ✅ Admin notifications created successfully via RPC");
      }

      return thread;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["support_threads"] });
    },
  });
}

// Hook to send a message
export function useSendSupportMessage() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({
      threadId,
      body,
      senderRole,
    }: {
      threadId: string;
      body: string;
      senderRole: "user" | "admin";
    }) => {
      if (!user?.id) throw new Error("Not authenticated");

      const { error } = await supabase.from("support_messages").insert({
        thread_id: threadId,
        sender_role: senderRole,
        sender_id: user.id,
        body,
      });

      if (error) throw error;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["support_messages", variables.threadId] });
      queryClient.invalidateQueries({ queryKey: ["support_threads"] });
    },
  });
}

// Hook to mark thread as read - disabled due to RLS issues
export function useMarkThreadRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (_threadId: string) => {
      // Disabled: support_thread_reads table has RLS issues causing 406 errors
      // Just return success without actually updating
      console.log("Thread read tracking disabled");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["support_threads"] });
      queryClient.invalidateQueries({ queryKey: ["support_unread_count"] });
    },
  });
}

// Hook to update thread (admin only)
export function useUpdateSupportThread() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      threadId,
      updates,
    }: {
      threadId: string;
      updates: Partial<Pick<SupportThread, "status" | "category" | "assigned_admin_id">>;
    }) => {
      const { error } = await supabase
        .from("support_threads")
        .update(updates)
        .eq("id", threadId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["support_threads"] });
    },
  });
}
